/**
 * One MutationObserver for everything that has to survive the aiWARE app-bar
 * widget rebuilding its DOM (VE-26426).
 *
 * Three things need to hear about that rebuild: the download icon re-asserts its
 * visibility and spin, and each of the two header tooltips re-finds the node it
 * anchors to. Each opening its own observer meant three document-wide `childList`
 * subtrees running for the life of the app — three traversals and three callbacks
 * for one event, when they all want the same signal.
 *
 * So they share one. It is created with the first subscriber and disconnected
 * after the last leaves, so an app with nothing to watch observes nothing.
 *
 * `childList` only, and deliberately: every subscriber responds by writing styles
 * and attributes, and observing those would have them retriggering each other.
 */
type Subscriber = () => void;

/**
 * A list rather than a `Set`, so each call registers in its own right.
 * `watchDownloadIcon` subscribes a stable module function, and a Set would fold
 * two registrations of it into one — after which either disposer would take the
 * observer away from a subscriber still relying on it. Notification order is
 * subscription order as a side benefit.
 */
const subscribers: Subscriber[] = [];
let observer: MutationObserver | undefined;

/**
 * Iterated over a copy so a subscriber that unsubscribes from inside its own
 * callback cannot make the list skip the one after it.
 */
const notify = () => {
  [...subscribers].forEach((subscriber) => subscriber());
};

/**
 * Run `subscriber` whenever the DOM changes, and return a disposer. The shared
 * observer goes away with the last subscriber, so this costs nothing when the
 * page has nothing to re-assert.
 */
export const observeWidgetDom = (subscriber: Subscriber): (() => void) => {
  if (typeof MutationObserver === 'undefined') {
    return () => undefined;
  }
  subscribers.push(subscriber);
  if (!observer) {
    observer = new MutationObserver(notify);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Disposing twice must not remove somebody else's registration of the same
  // function, which `indexOf` would happily do.
  let disposed = false;
  return () => {
    if (disposed) {
      return;
    }
    disposed = true;
    const at = subscribers.indexOf(subscriber);
    if (at !== -1) {
      subscribers.splice(at, 1);
    }
    if (subscribers.length === 0 && observer) {
      observer.disconnect();
      observer = undefined;
    }
  };
};

/** Test seam — the shared observer outlives a single test otherwise. */
export const resetWidgetDomWatcher = () => {
  observer?.disconnect();
  observer = undefined;
  subscribers.length = 0;
};
